// react-table depends on spreading props; we could name them explicitly but this component might
// break if the shape of the return value from row.getRowProps() changes
/* eslint-disable react/jsx-props-no-spreading */
// react-table is extending from object, so we have to do that as well
/* eslint-disable @typescript-eslint/ban-types */
import React from 'react';
import cx from 'classnames';
import { Cell, Row } from 'react-table';

interface SubHeaderProps<D extends object> {
    /**
     * Index to apply as the `aria-rowindex` attribute - see {@link https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Row_Role}
     */
    ariaRowIndex: number;
    /**
     * Sub header height
     */
    subHeaderHeight: number;
    /**
     * Outer row height
     */
    rowHeight: number;
    /**
     * The react-table row instance
     */
    row: Row<D>;
    /**
     * Tailwind size to apply to text
     */
    textSize: 'xs' | 'sm' | 'base' | 'lg';
}

/**
 * Renders the sub header for a grouped table row. This appears as a secondary header under the
 * grouped table row when it is expanded.
 * @param props - {@link SubHeaderProps}
 */
export function SubHeader<D extends object>({
    ariaRowIndex,
    subHeaderHeight,
    rowHeight,
    row,
    textSize,
}: SubHeaderProps<D>): JSX.Element {
    const { key: rowKey, ...rowRest } = row.getRowProps();

    return (
        <div
            key={`${rowKey}.InnerHeader`}
            {...rowRest}
            aria-rowindex={ariaRowIndex}
            style={{
                height: subHeaderHeight,
                top: rowHeight,
            }}
            className="tr inner-header-group flex items-end pb-1 sticky z-40 bg-white"
        >
            {row.cells.map((cell: Cell<D>) => {
                const { key: cellKey, ...cellRest } = cell.getCellProps();

                return (
                    <div
                        key={`${cellKey}.InnerHeader`}
                        {...cellRest}
                        className={cx(`th text-${textSize}`)}
                    >
                        {cell.render('InnerHeader')}
                    </div>
                );
            })}
        </div>
    );
}
